import { useEffect, useMemo, useState, type FC } from "react";
import type { Reservation, ReservationStatus } from "./reservations.types";
import { CalendarDays, Edit, FilterIcon, SearchIcon } from "lucide-react";
import { listReservations } from "../lib/api/reservations.apis";
import { useParams } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";

interface BookingsTabProps {
  STATUS_CONFIG: Record<ReservationStatus, { label: string; text: string; dot: string }>;
  setActiveTab: (tab: string, reservationId?: string) => void;
}

const toUiReservation = (reservation: any): Reservation => ({
  id: reservation._id,
  guestName: reservation.guestName,
  email: reservation.email || "-",
  partySize: reservation.partySize,
  section: "",
  createdAt: reservation.createdAt || reservation.startTime,
  status: reservation.status,
  tableAllocated: "-",
  startTime: reservation.startTime,
  endTime: reservation.endTime,
  phone: reservation.phone,
  specialRequest: reservation.specialRequest,
  notesInternal: reservation.notesInternal,
  source: reservation.source,
});

const BookingsTab: FC<BookingsTabProps> = ({ STATUS_CONFIG, setActiveTab }) => {
  const [filters, setFilters] = useState({
    channel: "all",
    status: "all",
    payment: "all",
    dateFrom: "",
    dateTo: "",
    search: "",
  });
  const [results, setResults] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);

  const { restaurantId = "" } = useParams();
  const { accessToken } = useAuth();

  const filteredByDate = useMemo(() => {
    if (!filters.dateFrom && !filters.dateTo) return results;

    const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const to = filters.dateTo ? new Date(filters.dateTo) : null;

    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return results.filter((reservation) => {
      const createdAt = reservation.createdAt ? new Date(reservation.createdAt) : null;
      if (!createdAt) return false;
      if (from && createdAt < from) return false;
      if (to && createdAt > to) return false;
      return true;
    });
  }, [results, filters.dateFrom, filters.dateTo]);

  const handleSearch = async () => {
    if (!restaurantId) {
      setError("Restaurant ID is missing.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await listReservations(
        restaurantId,
        {
          status: filters.status !== "all" ? filters.status : undefined,
          guest: filters.search || undefined,
          date: filters.dateFrom || undefined,
        },
        { token: accessToken }
      );
      // console.log("API Response:", response);
      setResults((response.reservations || []).map(toUiReservation));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!restaurantId || !accessToken) return;
    handleSearch();
  }, [restaurantId]);

  const filterInputCls =
    "px-3 py-2 border border-gray-200 rounded-lg text-xs text-stone-800 bg-gray-50 outline-none h-9 focus:border-orange-400 transition-colors";

  return (
    <div className="p-2 md:p-8">
      <div className="mb-1">
        <div className="flex justify-between">
          <h1 className="m-0 text-2xl font-bold text-stone-900">Bookings</h1>
          <div className="flex items-center gap-2">
            {[<CalendarDays />, <FilterIcon />].map((Icon, i) => (
              <button
                key={i}
                className="bg-stone-900 text-white hover:text-gray-600 rounded-lg p-2 transition-colors"
                onClick={() => (i === 0 ? setShowDateFilter((v) => !v) : setShowFilters((v) => !v))}
              >
                {Icon}
              </button>
            ))}
          </div>
        </div>
        <p className="m-0 mt-1 text-sm text-gray-400">Manage and track all reservations</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 px-2 md:px-6 py-3 md:py-5 mt-3 md:mt-5 mb-3 md:mb-6 shadow-sm">
        <p className="m-0 text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">Show report for:</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-36">
            <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Search</p>
            <input
              placeholder="Name, email..."
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className={`${filterInputCls} w-full`}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white border-none rounded-lg px-5 h-9 font-bold text-sm cursor-pointer flex items-center gap-2 transition-colors flex-shrink-0"
          >
            <SearchIcon /> {loading ? "Searching..." : "Search"}
          </button>
          {showDateFilter && (
            <>
              <div>
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Date From</p>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  className={filterInputCls}
                />
              </div>
              <div>
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Date To</p>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  className={filterInputCls}
                />
              </div>
            </>
          )}
          {showFilters && (
            <>
              <div>
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Channel</p>
                <select
                  value={filters.channel}
                  onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}
                  className={filterInputCls}
                >
                  <option value="all">All Channels</option>
                  <option value="walk_in">Walk-In</option>
                  <option value="email">Email</option>
                  <option value="gemio_app">Gemio App</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
              <div>
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Status</p>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className={filterInputCls}
                >
                  <option value="all">All Statuses</option>
                  {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                    <option key={v} value={v}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Payment</p>
                <select
                  value={filters.payment}
                  onChange={(e) => setFilters((f) => ({ ...f, payment: e.target.value }))}
                  className={filterInputCls}
                >
                  <option value="all">All Payment Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid px-5 py-2.5 bg-slate-50 rounded-xl mb-2" style={{ gridTemplateColumns: "2fr 1.2fr 1.2fr 1.2fr 0.8fr" }}>
        {["Guest", "Created On", "Status", "Table Allocated", ""].map((h, i) => (
          <div key={i} className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {h}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {!error && filteredByDate.length > 0
          ? filteredByDate.map((r) => {
              const s = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              return (
                <div
                  key={r.id}
                  className="grid px-2 md:px-5 py-3.5 bg-white rounded-2xl border border-gray-100 items-center shadow-sm hover:shadow-md transition-shadow"
                  style={{ gridTemplateColumns: "2fr 1.2fr 1.2fr 1.2fr 0.8fr" }}
                  onClick={() => setActiveTab("reservation-details", r.id)}
                >
                  <div>
                    <p className="m-0 text-sm font-bold text-stone-900">{r.guestName}</p>
                    <p className="m-0 hidden md:inline mt-0.5 text-[11px] text-gray-400">{r.email}</p>
                    <div className="flex gap-2 mt-1.5 flex-wrap">
                      <span className="text-[11px] bg-slate-100 text-gray-500 px-2 py-0.5 rounded-md">Party: {r.partySize}</span>
                      {r.section && <span className="text-[11px] bg-green-50 text-green-700 px-2 py-0.5 rounded-md">{r.section}</span>}
                    </div>
                  </div>
                  <p className="m-0 text-xs text-gray-500">
                    {r.createdAt
                      ? new Date(r.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                      : "-"}
                  </p>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                    <span className={`text-xs font-semibold ${s.text}`}>{s.label}</span>
                  </div>
                  <p className="m-0 text-xs font-medium text-stone-700">{r.tableAllocated || "-"}</p>
                  <button className="bg-stone-800 hover:bg-stone-700 text-white border-none rounded-lg px-2 py-1.5 text-xs font-semibold cursor-pointer transition-colors w-fit">
                    <Edit size={14} />
                  </button>
                </div>
              );
            })
          : !loading && !error && (
              <div className="text-center py-10 text-gray-400 text-sm">No bookings found matching your filters.</div>
            )}
      </div>
    </div>
  );
};

export default BookingsTab;
